import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { accounts as accountsApi, algos as algosApi, venues as venuesApi } from "../api/endpoints";
import type { Account, Algo, CreateAccountRequest } from "../api/types";
import { ApiError } from "../api/client";
import { AccountPicker } from "../components/AccountPicker";
import { ConnectionPill } from "../components/ConnectionPill";

// Risk defaults pre-filled in the Create-account form. starting_balance
// was ripped 2026-05-22 — the live balance comes from Rithmic's PNL
// plant, so there's no need to configure a "starting" amount that
// would drift from reality the moment a fill lands. NLV floor still
// has a sensible default ($600 = ~60% drawdown floor on a $1500 AMP
// micro-trading account); operator can override.
const DEFAULT_RISK = {
  max_contracts: 4,
  daily_loss_pct_balance: 20,
  nlv_floor_usd: 600,
};

// Production-market roster — all CME micro-index + MGC + MYM. Lilith
// trades 4 of these (mnq/mes/m2k/mym); the W53-55 calendar-event sleeves
// (fomc_minutes, beige_book, cpi_dir, post_fomc_short, opex_short, consensus_fade) all trade MNQ. Update this
// list when a new market is added to the production catalog.
const PRODUCTION_MARKETS = ["mnq", "mes", "m2k", "mym", "mgc"] as const;

export function SetupPage() {
  const qc = useQueryClient();
  const accountsQ = useQuery({
    queryKey: ["accounts"],
    queryFn: accountsApi.list,
    refetchInterval: 5000,
  });
  const algosQ = useQuery({
    queryKey: ["algos"],
    queryFn: algosApi.list,
    refetchInterval: 5000,
  });

  const list = accountsQ.data ?? [];
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  // Filter out retired accounts by default
  const visible = showRetired ? list : list.filter((a) => !a.retired);
  // Default to the most recently created non-retired account
  const account: Account | undefined = useMemo(() => {
    if (selectedId !== null) return list.find((a) => a.id === selectedId);
    return visible[visible.length - 1];
  }, [list, visible, selectedId]);

  return (
    <>
      {/* Always show the create form — supports multi-account */}
      <Step1Create
        existing={list}
        onCreated={(id) => setSelectedId(id)}
      />

      {list.length > 0 && (
        <>
          <div className="card" style={{ padding: "8px 16px" }}>
            <div className="row wrap" style={{ gap: 8 }}>
              <span className="muted small">Configure account:</span>
              <AccountPicker
                accounts={list}
                selectedId={account?.id ?? 0}
                onSelect={setSelectedId}
                showRetired={showRetired}
              />
              <div className="spacer" />
              <label className="row" style={{ gap: 4, cursor: "pointer", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={showRetired}
                  onChange={(e) => setShowRetired(e.target.checked)}
                  style={{ width: "auto", margin: 0 }}
                />
                <span className="small muted">show retired</span>
              </label>
            </div>
          </div>

          {account && (
            <AccountDetailCard
              account={account}
              onUpdated={() => qc.invalidateQueries({ queryKey: ["accounts"] })}
            />
          )}

          {account && account.broker === "rithmic" && <Step2ConnectRithmic account={account} />}
          {account && account.broker === "tradovate" && <Step2ConnectTradovate account={account} />}
          {account && (
            <Step3LinkAndStart
              account={account}
              algos={algosQ.data ?? []}
              loading={algosQ.isLoading}
            />
          )}
          {account && (account.rithmic?.status === "connected" || account.tradovate?.status === "connected") && (
            <Step4Done />
          )}
        </>
      )}
    </>
  );
}

// ── Step 1: create account (cash, prop firm, or sim) ────────────────────────

function Step1Create({
  existing,
  onCreated,
}: {
  existing: Account[];
  onCreated: (id: number) => void;
}) {
  const qc = useQueryClient();
  const venuesQ = useQuery({ queryKey: ["venues"], queryFn: venuesApi.list, staleTime: 300_000 });
  const [kind_, setKind] = useState<"rithmic_direct" | "tradovate" | "sim">("rithmic_direct");
  const [name, setName] = useState("AMP cash");
  const [maxC, setMaxC] = useState(String(DEFAULT_RISK.max_contracts));
  const [dailyPct, setDailyPct] = useState(String(DEFAULT_RISK.daily_loss_pct_balance));
  const [nlv, setNlv] = useState(String(DEFAULT_RISK.nlv_floor_usd));
  const [startingBalance, setStartingBalance] = useState("");
  const [shadowOf, setShadowOf] = useState<number | null>(null);
  const [assetClass, setAssetClass] = useState<"futures" | "crypto" | "options" | "equities" | "multi_asset">("futures");
  const [venue, setVenue] = useState("amp_futures");
  const [err, setErr] = useState<string | null>(null);

  const compatibleVenues = (venuesQ.data ?? []).filter((v) =>
    v.asset_classes.includes(assetClass) && (v.account_kinds.includes(kind_) || kind_ === "sim"),
  );
  const selectedVenue = compatibleVenues.some((v) => v.id === venue) ? venue : (compatibleVenues[0]?.id ?? "");
  const selectedVenueInfo = compatibleVenues.find((v) => v.id === selectedVenue);

  const isSim = kind_ === "sim";
  // Derive broker from kind — sim accounts don't have a broker.
  const broker = isSim ? undefined : kind_ === "rithmic_direct" ? "rithmic" : "tradovate";

  const liveAccounts = existing.filter((a) => a.kind !== "sim" && !a.retired);

  const create = useMutation({
    mutationFn: async () => {
      const body: CreateAccountRequest = {
        name,
        kind: kind_,
        broker,
        exec_mode: isSim ? "virtual" : "live",
        max_contracts: Number(maxC),
        daily_loss_pct_balance: Number(dailyPct),
        nlv_floor_usd: Number(nlv),
        portfolio_label: "default",
        starting_balance: isSim && startingBalance ? Number(startingBalance) : undefined,
        shadow_of: shadowOf ?? undefined,
        asset_class: assetClass,
        venue: selectedVenueInfo?.id,
      };
      const id = await accountsApi.create(body);
      return id;
    },
    onSuccess: (id) => {
      onCreated(id);
      setKind("rithmic_direct");
      setName("AMP cash");
      setMaxC(String(DEFAULT_RISK.max_contracts));
      setDailyPct(String(DEFAULT_RISK.daily_loss_pct_balance));
      setNlv(String(DEFAULT_RISK.nlv_floor_usd));
      setStartingBalance("");
      setShadowOf(null);
      setAssetClass("futures");
      setVenue("amp_futures");
      setErr(null);
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div className="card step active">
      <div className="step-title">
        1 · Create an account
        <small>cash (Rithmic), prop firm (Tradovate), or simulation</small>
      </div>
      {existing.length > 0 && (
        <div className="muted small" style={{ marginBottom: 12 }}>
          {existing.length} account(s): {existing.map((a) => a.name).join(", ")}
        </div>
      )}
      {err && <div className="error-banner">{err}</div>}
      <div className="row" style={{ marginBottom: 12 }}>
        <span className="label-text" style={{ marginRight: 8 }}>Account type:</span>
        <button
          className={kind_ === "rithmic_direct" ? "primary" : ""}
          onClick={() => { setKind("rithmic_direct"); setName("AMP cash"); }}
        >
          Cash account (Rithmic)
        </button>
        <button
          className={kind_ === "tradovate" ? "primary" : ""}
          onClick={() => { setKind("tradovate"); setName("Apex 50K"); }}
        >
          Prop firm account (Tradovate)
        </button>
        <button
          className={kind_ === "sim" ? "primary" : ""}
          onClick={() => { setKind("sim"); setName("Sim account"); setShadowOf(null); }}
        >
          Sim account
        </button>
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <span className="label-text" style={{ marginRight: 8 }}>Kind:</span>
        <span className="pill kind" style={{ fontSize: "0.85rem", padding: "2px 8px" }}>
          {kind_ === "rithmic_direct" ? "Rithmic Direct" : kind_ === "tradovate" ? "Tradovate" : "Sim"}
        </span>
        <span className="muted" style={{ marginLeft: 8, fontSize: "0.8rem" }}>
          {isSim ? "no broker needed — fills from bar prices" : "(auto-set from account type)"}
        </span>
      </div>
      <div className="grid grid-2">
        <label>
          <span className="label-text">Asset class / venue scope</span>
          <select value={assetClass} onChange={(e) => setAssetClass(e.target.value as typeof assetClass)}>
            <option value="futures">Futures</option>
            <option value="crypto">Crypto</option>
            <option value="options">Options</option>
            <option value="equities">Equities</option>
            <option value="multi_asset">Multi-asset</option>
          </select>
          <span className="label-help">What this real or simulated account is allowed to trade.</span>
        </label>
        <label>
          <span className="label-text">Venue / provider</span>
          <select value={venue} onChange={(e) => setVenue(e.target.value)}>
            {compatibleVenues.map((v) => (
              <option key={v.id} value={v.id} disabled={!v.live_enabled && !isSim}>{v.label} — {v.execution_status}</option>
            ))}
          </select>
          <span className="label-help">Only venues in the backend catalog can be selected.</span>
        </label>
        <label>
          <span className="label-text">Account name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          <span className="label-text">Max contracts</span>
          <input value={maxC} onChange={(e) => setMaxC(e.target.value)} inputMode="numeric" />
          <span className="label-help">hard ceiling on signed qty per dispatch (across all sleeves)</span>
        </label>
        {isSim ? (
          <>
            <label>
              <span className="label-text">Starting balance ($)</span>
              <input value={startingBalance} onChange={(e) => setStartingBalance(e.target.value)} inputMode="decimal" placeholder="10000" />
              <span className="label-help">simulated account equity (optional, default 0)</span>
            </label>
            <label>
              <span className="label-text">Twin live account</span>
              <select value={shadowOf ?? ""} onChange={(e) => setShadowOf(e.target.value ? Number(e.target.value) : null)} style={{ width: "100%" }}>
                <option value="">— free-floating (no twin) —</option>
                {liveAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} (#{a.id})</option>
                ))}
              </select>
              <span className="label-help">optional: link to a live account for LIVE-vs-SIM comparison</span>
            </label>
          </>
        ) : (
          <>
            <label>
              <span className="label-text">Daily loss cap (% of balance)</span>
              <input value={dailyPct} onChange={(e) => setDailyPct(e.target.value)} inputMode="decimal" />
              <span className="label-help">applied to whatever Rithmic reports as your live balance</span>
            </label>
            <label>
              <span className="label-text">NLV floor ($)</span>
              <input value={nlv} onChange={(e) => setNlv(e.target.value)} inputMode="decimal" />
              <span className="label-help">hard total-loss stop in dollars (independent of starting balance)</span>
            </label>
          </>
        )}
      </div>
      <hr />
      <div className="row">
        <span className="muted small">
          {isSim
            ? "Sim accounts are isolated — trades never hit a real broker."
            : "Created as a live-capable account; no orders are sent until a link is set to Live."}
        </span>
        <div className="spacer" />
        <button className="primary" disabled={create.isPending || !name || selectedVenueInfo == null} onClick={() => create.mutate()}>
          {create.isPending ? "creating…" : "Create account"}
        </button>
      </div>
    </div>
  );
}

// ── Step 2: connect Rithmic ───────────────────────────────────────────────

function Step2ConnectRithmic({ account }: { account: Account }) {
  const qc = useQueryClient();
  const [systemName, setSystemName] = useState("Rithmic 01");
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [acctNum, setAcctNum] = useState("");
  const [err, setErr] = useState<string | null>(null);
  // When status="error" we show a Retry-with-saved-creds panel by
  // default and hide the full re-enter-creds form behind a toggle.
  // Most error states are transient (Rithmic maintenance window /
  // R-Trader Pro briefly held the session) so the operator's saved
  // creds are still good — they just need to ask the engine to try
  // again. Empty form fields used to make this look like "you got
  // logged out" when in reality the creds are saved server-side.
  const [showReenter, setShowReenter] = useState(false);

  const status = account.rithmic?.status;
  const done = status === "connected";
  const connecting = status === "pending";
  const errored = status === "error" || (status && status !== "connected" && status !== "pending");
  const lastError = account.rithmic?.last_error;
  // "Has saved creds" = the account row has been through at least one
  // connect attempt that persisted rithmic_config_json. The server
  // stamps a status string the moment /rithmic/connect runs (even if
  // the handshake then fails), so ANY non-null status means the
  // creds are in the DB. A fresh account before its first connect
  // has account.rithmic === null and falls back to the full form.
  const hasSavedCreds = Boolean(account.rithmic && status);

  const connect = useMutation({
    mutationFn: () =>
      accountsApi.rithmicConnect(account.id, {
        env: "live",
        system_name: systemName,
        username: user,
        password: pass,
        // If left blank, the engine falls back to rows[0] from
        // Rithmic's account list — which can be the wrong account if
        // the credentials cover multiple. When AMP/Rithmic returns
        // `rp_code=1088 user has no permission to this account`, the
        // fix is to pin THIS field to the right account number.
        account_id: acctNum.trim() || undefined,
      }),
    onSuccess: () => {
      setUser("");
      setPass("");
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const retry = useMutation({
    mutationFn: () => accountsApi.rithmicRetry(account.id),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div className={"card step " + (done ? "done" : "active")}>
      <div className="step-title">
        2 · Connect to Rithmic
        <small>
          <ConnectionPill account={account} />
        </small>
      </div>
      {done ? (
        <div className="muted small">connected · system {systemName} · live</div>
      ) : errored && hasSavedCreds && !showReenter ? (
        // ── Errored-but-creds-saved state ───────────────────────────────
        // The most recent handshake failed but the credentials, system,
        // and account number are still saved on the row. 99% of error
        // recoveries are "try the same thing again after Rithmic
        // maintenance" or "close R-Trader Pro and try again." Default
        // the operator into the Retry path; only fall back to the full
        // form when they explicitly ask to re-enter.
        <>
          {err && <div className="error-banner">{err}</div>}
          {lastError && (
            <div className="error-banner">last error: {lastError}</div>
          )}
          <div className="muted small" style={{ marginBottom: 12 }}>
            Credentials are still saved (you don't need to re-enter them).
            The latest connection attempt failed; click Retry below to use
            the same credentials. Re-enter only if the saved creds have
            actually changed.
          </div>
          <div className="row">
            <button
              className="primary"
              disabled={retry.isPending || connecting}
              onClick={() => retry.mutate()}
            >
              {retry.isPending ? "retrying…" : "↻ Retry with saved credentials"}
            </button>
            <button onClick={() => setShowReenter(true)} disabled={retry.isPending}>
              Or re-enter credentials
            </button>
            <div className="spacer" />
            <span className="small muted">
              {connecting
                ? "handshake in flight…"
                : "transient errors (Rithmic maintenance, R-Trader Pro conflict) usually fix themselves on retry"}
            </span>
          </div>
        </>
      ) : (
        <>
          {err && <div className="error-banner">{err}</div>}
          {lastError && (
            <div className="error-banner">last error: {lastError}</div>
          )}
          {errored && hasSavedCreds && showReenter && (
            <div className="muted small" style={{ marginBottom: 8 }}>
              Re-entering credentials. <a
                href="#"
                onClick={(e) => { e.preventDefault(); setShowReenter(false); }}
              >
                ← back to retry-with-saved
              </a>
            </div>
          )}
          <div className="info-banner">
            AMP cash accounts route through Rithmic's production system{" "}
            <span className="mono">"Rithmic 01"</span> (pre-filled). If your AMP credentials email
            specifies a different system, paste it below.
            <br />
            <br />
            <strong>Error codes you may hit:</strong>
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
              <li><span className="mono">1067</span> — wrong system_name; check the AMP email</li>
              <li>
                <span className="mono">1088</span> "user has no permission to this account" — in
                order of likelihood:{" "}
                <strong>(1) Rithmic maintenance window</strong> (Friday evening through Sunday
                noon ET, daily 5:15–5:45 PM ET; ORDER plant works during this window but PNL
                plant rejects — retry after the window);{" "}
                <strong>(2) PnL API entitlement not granted on your Rithmic user</strong> —
                contact AMP and ask them to enable R|API PnL subscription for your user on this
                account;{" "}
                <strong>(3) same creds are open in R-Trader Pro right now</strong> — close
                R-Trader Pro and retry;{" "}
                <strong>(4) wrong account number</strong> when your credentials cover multiple
                accounts — pin it in the field below.
              </li>
            </ul>
          </div>
          <div className="grid grid-2">
            <label>
              <span className="label-text">System name</span>
              <input value={systemName} onChange={(e) => setSystemName(e.target.value)} />
            </label>
            <label>
              <span className="label-text">Environment</span>
              <input value="live" disabled />
            </label>
            <label>
              <span className="label-text">Rithmic username</span>
              <input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" />
            </label>
            <label>
              <span className="label-text">Rithmic password</span>
              <input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="span-2">
              <span className="label-text">Account number (optional)</span>
              <input
                value={acctNum}
                onChange={(e) => setAcctNum(e.target.value)}
                autoComplete="off"
                placeholder="e.g. APEX-12345 — leave blank to use Rithmic's first account"
              />
              <span className="label-help">
                AMP/Rithmic gives you a specific account number per credential set. Required
                when your credentials map to multiple accounts. If you leave it blank and see
                "STARTING — NO BALANCE FROM RITHMIC YET" forever, the engine picked the wrong
                one — come back and pin it.
              </span>
            </label>
          </div>
          <hr />
          <div className="row">
            <span className="muted small">
              {connecting ? "handshake in flight…" : "creds are sent over the tailnet, never stored in env."}
            </span>
            <div className="spacer" />
            <button
              className="primary"
              disabled={connect.isPending || !user || !pass}
              onClick={() => connect.mutate()}
            >
              {connect.isPending ? "connecting…" : "Connect"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Step 2 (Tradovate): connect prop firm account ──────────────────────────

function Step2ConnectTradovate({ account }: { account: Account }) {
  const qc = useQueryClient();
  const [env, setEnv] = useState<"demo" | "live">("live");
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const status = account.tradovate?.status;
  const done = status === "connected";
  const connecting = status === "pending";
  const errored = status === "error" || (status && status !== "connected" && status !== "pending");
  const lastError = account.tradovate?.last_error;
  const hasSavedCreds = Boolean(account.tradovate && status);

  const connect = useMutation({
    mutationFn: () =>
      accountsApi.tradovateConnect(account.id, { env, username: user, password: pass }),
    onSuccess: () => {
      setUser("");
      setPass("");
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const retry = useMutation({
    mutationFn: () => accountsApi.tradovateRetry(account.id),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const disconnect = useMutation({
    mutationFn: () => accountsApi.tradovateDisconnect(account.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div className={"card step " + (done ? "done" : "active")}>
      <div className="step-title">
        2 · Connect to Tradovate
        <small>
          <ConnectionPill account={account} />
        </small>
      </div>
      {done ? (
        <div className="muted small">connected · {env}</div>
      ) : errored && hasSavedCreds ? (
        <>
          {err && <div className="error-banner">{err}</div>}
          {lastError && <div className="error-banner">last error: {lastError}</div>}
          <div className="row">
            <button
              className="primary"
              disabled={retry.isPending || connecting}
              onClick={() => retry.mutate()}
            >
              {retry.isPending ? "retrying…" : "↻ Retry with saved credentials"}
            </button>
            <button onClick={() => disconnect.mutate()} className="danger">
              Disconnect
            </button>
            <div className="spacer" />
            <span className="small muted">
              {connecting ? "handshake in flight…" : "Use Retry for transient errors, Disconnect to clear saved creds"}
            </span>
          </div>
        </>
      ) : (
        <>
          {err && <div className="error-banner">{err}</div>}
          {lastError && <div className="error-banner">last error: {lastError}</div>}
          <div className="grid grid-2">
            <label>
              <span className="label-text">Environment</span>
              <select value={env} onChange={(e) => setEnv(e.target.value as "demo" | "live")}>
                <option value="live">Live</option>
                <option value="demo">Demo</option>
              </select>
            </label>
            <label>
              <span className="label-text">Tradovate username</span>
              <input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" />
            </label>
            <label>
              <span className="label-text">Tradovate password</span>
              <input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                autoComplete="off"
              />
            </label>
          </div>
          <hr />
          <div className="row">
            <span className="muted small">
              {connecting ? "handshake in flight…" : "creds are sent over the tailnet, never stored in plaintext."}
            </span>
            <div className="spacer" />
            <button
              className="primary"
              disabled={connect.isPending || !user || !pass}
              onClick={() => connect.mutate()}
            >
              {connect.isPending ? "connecting…" : "Connect"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Step 3: link the 4 sleeves and start them ─────────────────────────────

function Step3LinkAndStart({
  account,
  algos,
  loading,
}: {
  account: Account;
  algos: Algo[];
  loading: boolean;
}) {
  const qc = useQueryClient();
  const linksQ = useQuery({
    queryKey: ["account", account.id, "links"],
    queryFn: () => accountsApi.links(account.id),
    refetchInterval: 5000,
  });

  // All production algos — Lilith (4) + W53-55 calendar/consensus sleeves
  // (cpi_dir, post_fomc_short, opex_short, fomc_minutes, beige_book, consensus_fade). Filter by non-retired +
  // market ∈ production roster. Update PRODUCTION_MARKETS when expanding.
  const productionAlgos = useMemo(() => {
    const scope = account.asset_class;
    return algos.filter((a) => {
      if (a.retired) return false;
      const market = a.market.toLowerCase();
      if (!scope || scope === "multi_asset") return PRODUCTION_MARKETS.includes(market as (typeof PRODUCTION_MARKETS)[number]);
      if (scope === "futures") return PRODUCTION_MARKETS.includes(market as (typeof PRODUCTION_MARKETS)[number]);
      if (scope === "crypto") return market.toUpperCase().endsWith("USDT");
      if (scope === "options") return market.startsWith("opt_") || market.endsWith("_options");
      return false;
    });
  }, [algos, account.asset_class]);
  const scopeUnsupported = productionAlgos.length === 0;
  const sleeveCount = productionAlgos.length;
  const linkedIds = new Set((linksQ.data ?? []).filter((l) => l.active).map((l) => l.algo_id));
  const allLinked =
    productionAlgos.length > 0 && productionAlgos.every((a) => linkedIds.has(a.id));
  const runningIds = new Set(algos.filter((a) => a.status === "running").map((a) => a.id));
  const allRunning =
    productionAlgos.length > 0 && productionAlgos.every((a) => runningIds.has(a.id));

  const [actionErr, setActionErr] = useState<string | null>(null);

  const linkAll = useMutation({
    mutationFn: async () => {
      // POST creates the link active AND triggers ensure_live_runner in one
      // round trip. No separate /start call — the engine's `/algos/:id/start`
      // path opens a DBN file that isn't present on the engine VPS and
      // silently fails. Links carry no mode: execution semantics come from
      // the account's kind (this flow runs against the live cash account).
      for (const a of productionAlgos) {
        await accountsApi.linkAlgo(account.id, {
          algo_id: a.id,
          contracts: 1,
        });
      }
    },
    onSuccess: () => setActionErr(null),
    onError: (e) =>
      setActionErr(e instanceof ApiError ? e.message : String(e)),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["account", account.id, "links"] });
      qc.invalidateQueries({ queryKey: ["algos"] });
    },
  });

  // Re-fire ensure_live_runner for sleeves that aren't running — idempotent
  // nudge for the "I linked but the runner didn't spawn yet" case (e.g. the
  // ticker plant wasn't connected when the link landed). Sends the same
  // POST again so the server runs through ensure_live_runner a second time.
  // No DBN file involved.
  const startAll = useMutation({
    mutationFn: async () => {
      for (const a of productionAlgos) {
        if (!runningIds.has(a.id)) {
          await accountsApi.linkAlgo(account.id, {
            algo_id: a.id,
            contracts: 1,
          });
        }
      }
    },
    onSuccess: () => setActionErr(null),
    onError: (e) =>
      setActionErr(e instanceof ApiError ? e.message : String(e)),
    onSettled: () => qc.invalidateQueries({ queryKey: ["algos"] }),
  });

  const done = allLinked && allRunning;
  const cls = "card step " + (done ? "done" : "active");

  if (loading) {
    return (
      <div className={cls}>
        <div className="step-title">3 · Arm the sleeves</div>
        <div className="muted small">loading algos…</div>
      </div>
    );
  }
  if (productionAlgos.length === 0) {
    return (
      <div className={cls}>
        <div className="step-title">3 · Arm the sleeves</div>
        <div className={scopeUnsupported && account.asset_class ? "info-banner" : "error-banner"}>
          {account.asset_class
            ? `No ${account.asset_class} sleeves are currently registered for this account scope.`
            : "No compatible production sleeves found. Seed them on the server and refresh."}
        </div>
      </div>
    );
  }

  return (
    <div className={cls}>
      <div className="step-title">
        3 · Arm the sleeves at 1 contract each
        {done && <small>done · {sleeveCount} sleeves running</small>}
      </div>
      {actionErr && <div className="error-banner">{actionErr}</div>}
      <table>
        <thead>
          <tr>
            <th>Sleeve</th>
            <th>Market</th>
            <th>Linked</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {productionAlgos.map((a) => (
            <tr key={a.id}>
              <td>{a.name}</td>
              <td className="muted">{a.market}</td>
              <td>
                {linkedIds.has(a.id) ? (
                  <span className="pill good">linked</span>
                ) : (
                  <span className="pill dim">not linked</span>
                )}
              </td>
              <td>
                {a.status === "running" ? (
                  <span className="pill good">running</span>
                ) : (
                  <span className="pill dim">{a.status}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <hr />
      <div className="row">
        <button onClick={() => linkAll.mutate()} disabled={linkAll.isPending || allLinked}>
          {allLinked ? "✓ All linked" : linkAll.isPending ? "linking…" : `Link ${sleeveCount} sleeves to account`}
        </button>
        <button
          className="primary"
          onClick={() => startAll.mutate()}
          disabled={startAll.isPending || !allLinked || allRunning}
        >
          {allRunning ? "✓ All running" : startAll.isPending ? "starting…" : `Start ${sleeveCount} sleeves`}
        </button>
        <div className="spacer" />
        <span className="small muted">Sim links fill locally; live-capable links execute only when mode is Live.</span>
      </div>
    </div>
  );
}

// ── Step 4: done banner with a clear "next" pointer ───────────────────────

function Step4Done() {
  const navigate = useNavigate();
  return (
    <div className="card step done">
      <div className="step-title">
        4 · You're armed
      </div>
      <div className="muted small">
        Open the Desk view to validate signals and connection on live bars. Sim accounts remain
        isolated; live-capable accounts send orders only through links explicitly set to Live.
      </div>
      <hr />
      <button className="primary" onClick={() => navigate("/desk")}>
        Open Desk →
      </button>
    </div>
  );
}

// ── Account Detail Card: kind badge, edit, retire ─────────────────────────

/**
 * Human-readable labels for account kinds.
 */
const KIND_LABELS: Record<string, string> = {
  sim: "Sim",
  rithmic_direct: "Rithmic Direct",
  tradovate: "Tradovate",
  sierra_bridged: "Sierra Bridged",
  topstepx: "TopstepX",
};

function AccountDetailCard({
  account,
  onUpdated,
}: {
  account: Account;
  onUpdated: () => void;
}) {
  const [editing, setEditing] = useState(false);
  // Edit form state (initialised from account)
  const [editName, setEditName] = useState(account.name);
  const [editMaxC, setEditMaxC] = useState(String(account.max_contracts ?? ""));
  const [editDailyPct, setEditDailyPct] = useState(
    String(account.daily_loss_pct_balance ?? ""),
  );
  const [editNlv, setEditNlv] = useState(
    String(account.nlv_floor_usd ?? ""),
  );
  const [editPortfolioLabel, setEditPortfolioLabel] = useState(
    account.portfolio_label ?? "",
  );
  const [editErr, setEditErr] = useState<string | null>(null);
  const [retireConfirm, setRetireConfirm] = useState(false);
  const [retireErr, setRetireErr] = useState<string | null>(null);

  // Reset edit form when switching accounts
  const stableId = account.id;
  const prevIdRef = useRef(stableId);
  if (prevIdRef.current !== stableId) {
    prevIdRef.current = stableId;
    setEditName(account.name);
    setEditMaxC(String(account.max_contracts ?? ""));
    setEditDailyPct(String(account.daily_loss_pct_balance ?? ""));
    setEditNlv(String(account.nlv_floor_usd ?? ""));
    setEditPortfolioLabel(account.portfolio_label ?? "");
    setEditing(false);
    setRetireConfirm(false);
    setEditErr(null);
    setRetireErr(null);
  }

  const save = useMutation({
    mutationFn: () =>
      accountsApi.patch(account.id, {
        name: editName,
        max_contracts: editMaxC ? Number(editMaxC) : undefined,
        daily_loss_pct_balance: editDailyPct ? Number(editDailyPct) : undefined,
        nlv_floor_usd: editNlv ? Number(editNlv) : undefined,
        portfolio_label: editPortfolioLabel || undefined,
      }),
    onSuccess: () => {
      setEditing(false);
      setEditErr(null);
      onUpdated();
    },
    onError: (e) => setEditErr(e instanceof ApiError ? e.message : String(e)),
  });

  const doRetire = useMutation({
    mutationFn: () => accountsApi.retire(account.id),
    onSuccess: () => {
      setRetireConfirm(false);
      setRetireErr(null);
      onUpdated();
    },
    onError: (e) =>
      setRetireErr(e instanceof ApiError ? e.message : String(e)),
  });

  const kindLabel = KIND_LABELS[account.kind] ?? account.kind;
  const isRetired = account.retired;

  return (
    <div
      className="card"
      style={{
        opacity: isRetired ? 0.55 : 1,
        ...(isRetired ? { pointerEvents: "none" as const } : {}),
      }}
    >
      <div className="row" style={{ marginBottom: 6 }}>
        <span className="step-title" style={{ margin: 0 }}>
          {account.name}
          <span className="mono muted" style={{ marginLeft: 6, fontSize: 11 }}>
            #{account.id}
          </span>
        </span>
        <span className="pill kind">{kindLabel}</span>
        {isRetired && <span className="pill retired-pill">retired</span>}
        {account.shadow_of != null && (
          <span className="small muted">
            shadow of #{account.shadow_of}
          </span>
        )}
        <div className="spacer" />
        {!isRetired && (
          <>
            <button
              className="small-btn"
              onClick={() => {
                setEditing(!editing);
                setRetireConfirm(false);
                if (!editing) {
                  // Re-initialise fields on open
                  setEditName(account.name);
                  setEditMaxC(String(account.max_contracts ?? ""));
                  setEditDailyPct(String(account.daily_loss_pct_balance ?? ""));
                  setEditNlv(String(account.nlv_floor_usd ?? ""));
                  setEditPortfolioLabel(account.portfolio_label ?? "");
                }
              }}
            >
              {editing ? "Cancel" : "✎ Edit"}
            </button>
            <button
              className="small-btn"
              style={{ borderColor: "var(--bad)", color: "var(--bad)" }}
              onClick={() => {
                setRetireConfirm(true);
                setEditing(false);
              }}
            >
              Retire
            </button>
          </>
        )}
      </div>

      {/* ── Inline editor ─────────────────────────────── */}
      {editing && (
        <>
          {editErr && <div className="error-banner">{editErr}</div>}
          <div className="grid grid-2" style={{ marginBottom: 8 }}>
            <label>
              <span className="label-text">Name</span>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </label>
            <label>
              <span className="label-text">Max contracts</span>
              <input
                value={editMaxC}
                onChange={(e) => setEditMaxC(e.target.value)}
                inputMode="numeric"
              />
            </label>
            <label>
              <span className="label-text">Daily loss cap (% of balance)</span>
              <input
                value={editDailyPct}
                onChange={(e) => setEditDailyPct(e.target.value)}
                inputMode="decimal"
              />
            </label>
            <label>
              <span className="label-text">NLV floor ($)</span>
              <input
                value={editNlv}
                onChange={(e) => setEditNlv(e.target.value)}
                inputMode="decimal"
              />
            </label>
            <label className="span-2">
              <span className="label-text">Portfolio label</span>
              <input
                value={editPortfolioLabel}
                onChange={(e) => setEditPortfolioLabel(e.target.value)}
                placeholder="e.g. pareto-4"
              />
            </label>
          </div>
          <hr />
          <div className="row">
            <div className="spacer" />
            <button
              className="primary"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "saving…" : "Save changes"}
            </button>
          </div>
        </>
      )}

      {/* ── Retire confirmation ────────────────────────── */}
      {retireConfirm && !isRetired && (
        <>
          {retireErr && <div className="error-banner">{retireErr}</div>}
          <div
            className="info-banner"
            style={{ borderColor: "rgba(255, 82, 82, 0.3)" }}
          >
            <strong>Retire this account?</strong>
            <br />
            The account will be soft-deleted. Active algo links will be
            deactivated. No further trading will be dispatched. Trade history
            is preserved and still visible.
            {account.open_positions && account.open_positions.length > 0 && (
              <>
                <br />
                <br />
                <span className="bad">
                  ⚠ {account.open_positions.length} open position(s) — retire
                  will be rejected until all positions are closed.
                </span>
              </>
            )}
          </div>
          <div className="row">
            <button
              className="danger"
              disabled={doRetire.isPending}
              onClick={() => doRetire.mutate()}
            >
              {doRetire.isPending ? "retiring…" : "✓ Confirm retire"}
            </button>
            <button
              onClick={() => {
                setRetireConfirm(false);
                setRetireErr(null);
              }}
              disabled={doRetire.isPending}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}
